import useId from '@hooks/use-id';
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import useNode from '@hooks/use-node';
import useUnfocusedListControls from '@hooks/use-unfocused-list-controls';
import { logger } from '@modules/logger';
import { useComposedRefs } from '@reach/utils';
import {
  type FC,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './InlineCombobox.module.scss';

interface inlineComboboxContextValues {
  onSelect: (value: string) => void;
  highlightedValue: string | undefined;
  handleChangeHighlight: (value: string) => void;
  idGenerator: (value: string | undefined) => string;
  listRef: (instance: HTMLUListElement | null) => void;
  itemsRef: MutableRefObject<string[]>;
}

const initialContext = {
  onSelect: () => logger.warn('onSelect not provided for inlineComboboxContext'),
  highlightedValue: undefined,
  handleChangeHighlight: () =>
    logger.warn('handleChangeHighlight not provided for inlineComboboxContext'),
  idGenerator: () => {
    logger.warn('idGenerator not provided for inlineComboboxContext');
    return '';
  },
  listRef: () => logger.warn('listRef not provided for inlineComboboxContext'),
  itemsRef: { current: [] },
};

const inlineComboboxContext = createContext<inlineComboboxContextValues>(initialContext);

interface InlineComboboxListItemProps {
  value: string;
  children: ReactNode;
  selected?: boolean;
}

const InlineComboboxListItem: FC<PropsWithChildren<InlineComboboxListItemProps>> = ({
  children,
  value,
  selected = false,
}) => {
  const { itemsRef, idGenerator, highlightedValue, onSelect } = useContext(inlineComboboxContext);
  const highlighted = value === highlightedValue;

  useEffect(() => {
    itemsRef.current.push(value);
  });

  return (
    <li
      id={idGenerator(value)}
      className={styles['inline-combobox-list-item']}
      role="option"
      data-active={selected}
      aria-selected={highlighted}
      //onMouseOver={() => handleChangeHighlight(value)}
      onFocus={() => undefined}
    >
      <button type="button" onClick={() => onSelect(value)} tabIndex={-1}>
        {children}
      </button>
    </li>
  );
};

interface InlineComboboxProps {
  show: boolean;
  onSelect: (value: string) => void;
  containerNode?: HTMLElement;
}

const InlineCombobox: FC<PropsWithChildren<InlineComboboxProps>> = ({
  show,
  onSelect,
  children,
  containerNode,
}) => {
  const [listNode, listRef] = useNode<HTMLUListElement>();
  const [highlightedValue, setHighlightedValue] = useState<string>();
  const listId = useId();
  const itemsRef = useRef([]);
  const prevItemsRef = useRef([]);

  const handleChangeHighlight = useCallback((highlightedValue: string) => {
    setHighlightedValue(highlightedValue);
  }, []);

  useEffect(() => {
    setHighlightedValue(undefined);
  }, [show]);

  useEffect(() => {
    if (show) {
      setHighlightedValue(itemsRef.current[0]);
    }
  }, [show]);

  const idGenerator = (value: string | undefined) => (value ? `${listId}-${value}` : '');

  // This schedules the clearing of itemsRefs before the options push new content to it
  useIsomorphicLayoutEffect(() => {
    itemsRef.current = [];
    return () => {
      prevItemsRef.current = itemsRef.current;
      itemsRef.current = [];
    };
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!arrayEquals(itemsRef.current, prevItemsRef.current)) {
      setHighlightedValue(itemsRef.current[0]);
    }
  });

  const onKeyDown = useUnfocusedListControls({
    show,
    values: itemsRef.current,
    listNode,
    onSelect,
    highlightedValue,
    onChangeHighlight: handleChangeHighlight,
    containerNode,
  });

  return (
    <inlineComboboxContext.Provider
      value={{ listRef, itemsRef, highlightedValue, handleChangeHighlight, onSelect, idGenerator }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={styles['inline-combobox']} onKeyDown={onKeyDown}>
        {children}
      </div>
    </inlineComboboxContext.Provider>
  );
};

function arrayEquals(a: string[], b: string[]): boolean {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, index) => val === b[index])
  );
}

const InlineComboboxList = ({
  ref: forwardedRef,
  children,
}: { children: ReactElement } & {
  ref?: React.Ref<HTMLUListElement>;
}) => {
  const { highlightedValue, listRef, idGenerator } = useContext(inlineComboboxContext);

  const ref = useComposedRefs(listRef, forwardedRef);

  const listProps = {
    ref,
    'aria-activedescendant': idGenerator(highlightedValue),
    tabIndex: 0,
    role: 'listbox',
  };

  return <>{cloneElement(children, listProps)}</>;
};

InlineComboboxList.displayName = 'InlineComboboxList';

export default InlineCombobox;
export { InlineComboboxList, InlineComboboxListItem };
